"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlignLeft,
  BookOpen,
  Code2,
  FileText,
  Film,
  Image as ImageIcon,
  Layers,
  ListChecks,
  Loader2,
  type LucideIcon,
  MessageCircle,
  MousePointerClick,
  Sparkles,
  Sticker,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Block, BlockType, BlockStatus } from "@/lib/book-types";

const TYPE_ICON: Record<BlockType, LucideIcon> = {
  text: AlignLeft,
  section: BookOpen,
  callout: Sparkles,
  quiz: ListChecks,
  user_note: FileText,
  figure: ImageIcon,
  interactive: MousePointerClick,
  animation: Film,
  code: Code2,
  timeline: Layers,
  flash_cards: Sticker,
  deep_dive: MessageCircle,
  concept_graph: Layers,
};

const TYPE_LABEL_EN: Record<BlockType, string> = {
  text: "Text",
  section: "Section",
  callout: "Callout",
  quiz: "Quiz",
  user_note: "Note",
  figure: "Figure",
  interactive: "Interactive",
  animation: "Animation",
  code: "Code",
  timeline: "Timeline",
  flash_cards: "Flash cards",
  deep_dive: "Deep dive",
  concept_graph: "Concept graph",
};

function shortLabel(block: Block, fallback: string): string {
  const title = (block.title || "").trim();
  if (title) return title;
  const params = (block.params || {}) as Record<string, unknown>;
  const focus = typeof params.focus === "string" ? params.focus.trim() : "";
  if (focus) return focus;
  const role = typeof params.role === "string" ? params.role.trim() : "";
  if (role) return `${fallback} · ${role}`;
  const variant =
    typeof params.variant === "string" ? params.variant.trim() : "";
  if (variant) return `${fallback} · ${variant}`;
  return fallback;
}

function statusDotClass(status: BlockStatus): string {
  switch (status) {
    case "ready":
      return "bg-emerald-500";
    case "generating":
      return "bg-amber-400 animate-pulse";
    case "pending":
      return "bg-[var(--muted-foreground)]/40";
    case "error":
      return "bg-rose-500";
    case "hidden":
      return "bg-[var(--muted-foreground)]/20";
    default:
      return "bg-[var(--muted-foreground)]/40";
  }
}

export interface PageOutlineProps {
  blocks: Block[];
  /** Reader scroll element — the IntersectionObserver root that drives the
      active-block highlight. Lifted out of PageReader so this list can live
      in the sidebar. */
  scrollContainer?: HTMLElement | null;
}

/** Block-level outline for the page currently open in the reader. Rendered
    nested under the active chapter in the book sidebar. */
export default function PageOutline({
  blocks,
  scrollContainer,
}: PageOutlineProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const visibleBlocks = useMemo(
    () => blocks.filter((b) => b.status !== "hidden"),
    [blocks],
  );

  useEffect(() => {
    if (!scrollContainer || visibleBlocks.length === 0) return;
    const elements = visibleBlocks
      .map((b) => document.getElementById(`block-${b.id}`))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (intersecting.length > 0) {
          setActiveId(intersecting[0].target.id.replace(/^block-/, ""));
        }
      },
      { root: scrollContainer, rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [scrollContainer, visibleBlocks]);

  const handleJump = (blockId: string) => {
    const el = document.getElementById(`block-${blockId}`);
    if (!el) return;
    setActiveId(blockId);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (visibleBlocks.length === 0) return null;

  return (
    <ol className="mt-1 space-y-0.5 border-l border-[var(--border)] pl-2">
      {visibleBlocks.map((block, idx) => {
        const Icon = TYPE_ICON[block.type] || FileText;
        const fallbackLabel = t(TYPE_LABEL_EN[block.type] || block.type);
        const label = shortLabel(block, fallbackLabel);
        const isActive = block.id === activeId;
        const isError = block.status === "error";
        const isLoading =
          block.status === "pending" || block.status === "generating";
        return (
          <li key={block.id}>
            <button
              type="button"
              onClick={() => handleJump(block.id)}
              className={[
                "group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[13px] transition-colors duration-150",
                isActive
                  ? "bg-[var(--primary)]/10 text-[var(--foreground)]"
                  : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]/40 hover:text-[var(--foreground)]",
              ].join(" ")}
            >
              <span
                className={[
                  "shrink-0 transition-colors",
                  isActive
                    ? "text-[var(--primary)]"
                    : "text-[var(--muted-foreground)]",
                ].join(" ")}
              >
                {isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : isError ? (
                  <AlertCircle className="h-3 w-3 text-rose-500" />
                ) : (
                  <Icon className="h-3 w-3" />
                )}
              </span>
              <span className="w-4 shrink-0 tabular-nums text-[var(--muted-foreground)]">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="truncate" title={`${fallbackLabel} \u00b7 ${label}`}>
                {label}
              </span>
              <span
                className={[
                  "ml-auto h-1.5 w-1.5 shrink-0 rounded-full",
                  statusDotClass(block.status),
                ].join(" ")}
              />
            </button>
          </li>
        );
      })}
    </ol>
  );
}
